import { useState } from 'react';
import { fetchCommercialAddonProductOfferings } from '../../common/fetch.js';
import { t } from '../../common/i18n/index.js';
import {
  getLocalizedString,
  getPriceFromType,
  isMandatoryAddon,
  isMandatoryAddonGroup,
} from '../../common/utils/productOfferingsUtils.js';
import { PriceType } from '../../components/HorizontalSubscriptionCard/types.js';
import { addToShoppingBasketCoCo } from '../../components/ShoppingBasket/shoppingBasketFunctionsCoCo.js';
import type { ProductOrderProps } from '../../common/types/shoppingBasketCoCo.js';
import type { ProductOfferingCoco } from '../../generated/api/productOfferingCoco.js';
import type { ProductOfferingsResponse } from '../../generated/api/productOfferingsResponse.js';
import type { Dispatch, SetStateAction } from 'react';

export interface UseProductOfferingsCardCoCoReturn {
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  handleOpenModal: () => void;
  commercialAddonOfferings: ProductOfferingCoco[];
  quantity: number;
  setQuantity: Dispatch<SetStateAction<number>>;
  header: string;
  description?: string;
  oneTimeCharge: number;
  recurringCharge: number;
  baseOneTimeCharge: number;
  baseRecurringCharge: number;
  hasQuantity: boolean;
  buttonText: string;
  buttonMakesOrder: boolean;
  isChangeOffer: boolean;
  onOrder: ({ selectedProductOffers }: ProductOrderProps) => void;
  showFixedTermInfo: boolean;
}

// TODO: implement them later once backend support is available
const hasQuantity = false;
const showFixedTermInfo = true;
const buttonMakesOrder = false;
const isChangeOffer = false;

export const useProductOfferingsCardCoCo = (
  productOffering: ProductOfferingsResponse,
  qualificationAddressId?: string,
  qualificationAddressText?: string
): UseProductOfferingsCardCoCoReturn => {
  const [showModal, setShowModal] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [commercialAddonOfferings, setCommercialAddonOfferings] = useState<ProductOfferingCoco[]>([]);

  const handleOpenModal = async () => {
    try {
      const response = await fetchCommercialAddonProductOfferings(productOffering.id);
      const addons = response.flatMap(r => r.commercialAddonProductOffering ?? []);
      setCommercialAddonOfferings(addons);
    } catch {
      setCommercialAddonOfferings([]);
    }
    setShowModal(true);
  };

  const header = getLocalizedString(productOffering.name);
  const description = getLocalizedString(productOffering.description);

  const { planProductOffering } = productOffering;
  const planPrice = planProductOffering?.productOfferingPrice ?? [];
  const offeringPrice = productOffering.productOfferingPrice ?? [];

  const getEffectivePrice = (priceType: PriceType) =>
    getPriceFromType(planPrice, priceType) || getPriceFromType(offeringPrice, priceType);

  const mandatoryAddons = (productOffering.bundleAddonProductOffering ?? []).filter(isMandatoryAddon);
  const getMandatoryAddonsPrice = (priceType: PriceType) =>
    mandatoryAddons.reduce((sum, addon) => sum + getPriceFromType(addon.productOfferingPrice, priceType), 0);

  const mandatoryAddonGroupOfferings = (productOffering.bundleAddonGroup ?? [])
    .filter(group => isMandatoryAddonGroup(group))

    .flatMap(group => (group.productOffering ?? []).filter(addon => addon.isDefault === true));
  const getMandatoryAddonGroupPrice = (priceType: PriceType) =>
    mandatoryAddonGroupOfferings.reduce(
      (sum, addon) => sum + getPriceFromType(addon.productOfferingPrice, priceType),
      0
    );

  const baseOneTimeCharge = getEffectivePrice(PriceType.ONE_TIME_CHARGE);
  const baseRecurringCharge = getEffectivePrice(PriceType.RECURRING_CHARGE);
  const oneTimeCharge =
    baseOneTimeCharge +
    getMandatoryAddonsPrice(PriceType.ONE_TIME_CHARGE) +
    getMandatoryAddonGroupPrice(PriceType.ONE_TIME_CHARGE);
  const recurringCharge =
    baseRecurringCharge +
    getMandatoryAddonsPrice(PriceType.RECURRING_CHARGE) +
    getMandatoryAddonGroupPrice(PriceType.RECURRING_CHARGE);

  const buttonText = t.B5J1('Add to basket');

  const onOrder = ({ selectedProductOffers, quantity: orderQuantity }: ProductOrderProps) => {
    addToShoppingBasketCoCo(selectedProductOffers, orderQuantity, qualificationAddressId, qualificationAddressText);
  };

  return {
    showModal,
    setShowModal,
    handleOpenModal,
    commercialAddonOfferings,
    quantity,
    setQuantity,
    header,
    description,
    oneTimeCharge,
    recurringCharge,
    baseOneTimeCharge,
    baseRecurringCharge,
    hasQuantity,
    buttonText,
    buttonMakesOrder,
    isChangeOffer,
    onOrder,
    showFixedTermInfo,
  };
};
